/**
 * <TransactionContextPanel /> — renders the
 * `TransactionContext` payload alongside the HR-initiate form
 * (M30.5.2).
 *
 * Shows HR what the system already knows about the employee
 * before they fill the form. The five sections mirror the
 * backend snapshot fields:
 *
 *   - Active contract summary (basic salary + status + start/end)
 *   - Leave balances (per leave_type_code)
 *   - Active loans (outstanding balance + monthly installment)
 *   - Recent T&A count (last 30 days)
 *   - Current payroll period (open / finalised)
 *
 * Money values use <LtrAmount>; identifiers + period codes use
 * <LtrText>. Bilingual via i18next.
 *
 * The panel is a sidebar companion to the form — most forms
 * render it in a right column on desktop, collapsed on mobile.
 */

import { Briefcase, CalendarClock, ClipboardList, Coins, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LtrAmount, LtrText } from "../ui";

// Minimal shape matching the backend TransactionContext payload.
// Forms can pass partial data — every section degrades gracefully.

export interface ContractSnapshot {
  contract_id: string;
  contract_status: string | null;
  basic_salary: string | null;
  start_date: string | null;
  end_date: string | null;
  employment_type: string | null;
}

export interface LeaveBalanceSnapshot {
  leave_type_code: string;
  current_balance: string;
  balance_period: string;
}

export interface LoanOutstandingSnapshot {
  application_id: string;
  outstanding_balance: string;
  monthly_installment: string | null;
  workflow_state: string;
}

export interface PayrollPeriodSnapshot {
  period_year_month: string;
  is_finalised: boolean;
}

export interface TransactionContextSnapshot {
  employee_id: string;
  active_contract: ContractSnapshot | null;
  leave_balances?: Record<string, LeaveBalanceSnapshot>;
  loan_outstandings?: LoanOutstandingSnapshot[];
  recent_attendance_count?: number;
  pending_workflows?: number;
  current_period_payroll?: PayrollPeriodSnapshot | null;
}

export interface TransactionContextPanelProps {
  context: TransactionContextSnapshot;
  className?: string;
}

export function TransactionContextPanel({
  context,
  className,
}: TransactionContextPanelProps) {
  const { t } = useTranslation();
  const contract = context.active_contract;
  const balances = Object.values(context.leave_balances ?? {});
  const loans = context.loan_outstandings ?? [];
  const period = context.current_period_payroll;

  return (
    <aside
      className={[
        "rounded-lg border border-border-subtle bg-surface-raised p-4 shadow-sm",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={t("ai_panel.context_panel_title")}
    >
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
        <Briefcase aria-hidden className="h-4 w-4" />
        {t("ai_panel.context_panel_title")}
      </h3>

      {/* Active contract */}
      <Section
        icon={Briefcase}
        title={t("ai_panel.context_active_contract")}
      >
        {contract ? (
          <dl className="grid grid-cols-2 gap-1 text-xs text-text-secondary">
            <dt>{t("ai_panel.context_basic_salary")}</dt>
            <dd className="text-text-primary">
              {contract.basic_salary ? (
                <LtrAmount
                  value={Number(contract.basic_salary)}
                  currency="SAR"
                />
              ) : (
                "—"
              )}
            </dd>
            <dt>{t("hub_recent_state", { defaultValue: "Status" })}</dt>
            <dd className="text-text-primary">
              <LtrText>{contract.contract_status ?? "—"}</LtrText>
            </dd>
          </dl>
        ) : (
          <p className="text-xs text-text-secondary">—</p>
        )}
      </Section>

      {/* Leave balances */}
      <Section
        icon={CalendarClock}
        title={t("ai_panel.context_leave_balances")}
      >
        {balances.length === 0 ? (
          <p className="text-xs text-text-secondary">—</p>
        ) : (
          <ul className="flex flex-col gap-1 text-xs">
            {balances.map((b) => (
              <li
                key={b.leave_type_code}
                className="flex items-center justify-between"
              >
                <LtrText className="text-text-secondary">
                  {b.leave_type_code}
                </LtrText>
                <span className="font-medium text-text-primary">
                  {b.current_balance}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Active loans */}
      <Section icon={Coins} title={t("ai_panel.context_loan_outstandings")}>
        {loans.length === 0 ? (
          <p className="text-xs text-text-secondary">
            {t("ai_panel.context_no_loans")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-xs">
            {loans.map((lo) => (
              <li
                key={lo.application_id}
                className="flex items-center justify-between"
              >
                <LtrText className="text-text-secondary">
                  {lo.application_id.slice(0, 8)}
                </LtrText>
                <span className="font-medium text-text-primary">
                  <LtrAmount
                    value={Number(lo.outstanding_balance)}
                    currency="SAR"
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Recent T&A */}
      <Section
        icon={Timer}
        title={t("ai_panel.context_recent_attendance")}
      >
        <p className="text-xs text-text-secondary">
          {t("ai_panel.context_recent_attendance_count", {
            count: context.recent_attendance_count ?? 0,
          })}
        </p>
      </Section>

      {/* Pending workflows */}
      <Section
        icon={ClipboardList}
        title={t("ai_panel.context_pending_workflows")}
      >
        <p className="text-xs text-text-secondary">
          {(context.pending_workflows ?? 0) === 0
            ? t("ai_panel.context_no_pending")
            : context.pending_workflows}
        </p>
      </Section>

      {/* Payroll period */}
      {period && (
        <Section
          icon={CalendarClock}
          title={t("ai_panel.context_payroll_period")}
        >
          <p className="flex items-center gap-2 text-xs">
            <LtrText className="font-medium text-text-primary">
              {period.period_year_month}
            </LtrText>
            <span
              className={
                period.is_finalised
                  ? "text-state-denied"
                  : "text-state-approved"
              }
            >
              {period.is_finalised
                ? t("ai_panel.context_payroll_locked")
                : t("ai_panel.context_payroll_open")}
            </span>
          </p>
        </Section>
      )}
    </aside>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Briefcase;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 border-t border-border-subtle/40 pt-3 last:mb-0">
      <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        <Icon aria-hidden className="h-3 w-3" />
        {title}
      </h4>
      {children}
    </div>
  );
}
